import { ArrowRight, Building2, Eye, LockKeyhole, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { UserRole } from "../types";
import { useT } from "../i18n";

export default function LoginPage({
  demoEnabled,
  onLogin,
  onPreview,
  busy,
  error,
}: {
  demoEnabled: boolean;
  onLogin: (email: string, password: string) => Promise<void>;
  onPreview: (role: UserRole) => Promise<void>;
  busy: boolean;
  error: string;
}) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onLogin(email, password);
  }

  return (
    <main className="login-page">
      <section className="login-visual" aria-label={t("login.portalLabel")}>
        <img src="/brand/login-production.webp" alt={t("login.heroAlt")} />
        <div className="login-visual-shade" />
        <div className="login-brandline">
          {/* Prismic's mark, not a client's. Nobody is signed in yet, so there is
              no client to brand this page as. */}
          <img className="login-logo" src="/brand/prismic-logo.webp" alt="" />
          <span>Prismic Media</span>
        </div>
        <div className="login-message">
          <span>{t("login.portalLabel")}</span>
          <h1>{t("login.tagline1")}<br />{t("login.tagline2")}<br />{t("login.tagline3")}</h1>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-form-wrap">
          <div className="login-heading">
            <span>{t("login.secureAccess")}</span>
            <h2>{t("login.signInTitle")}</h2>
            <p>{t("login.intro")}</p>
          </div>

          <form onSubmit={submit} className="login-form">
            <label>
              <span>{t("login.email")}</span>
              <div className="input-with-icon">
                <Mail size={18} />
                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@company.com" required />
              </div>
            </label>
            <label>
              <span>{t("login.password")}</span>
              <div className="input-with-icon">
                <LockKeyhole size={18} />
                <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("login.passwordPlaceholder")} required />
              </div>
            </label>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="button button-primary login-submit" disabled={busy}>
              <span>{busy ? "Signing in…" : t("login.signIn")}</span>
              <ArrowRight size={18} />
            </button>
          </form>

          {demoEnabled && (
            <div className="preview-access">
              <div className="preview-divider"><span>{t("login.localPreview")}</span></div>
              <div className="preview-buttons">
                <button className="button button-secondary" onClick={() => onPreview("client")} disabled={busy}>
                  <Eye size={18} /> {t("login.clientView")}
                </button>
                <button className="button button-secondary" onClick={() => onPreview("admin")} disabled={busy}>
                  <Building2 size={18} /> {t("login.prismicView")}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="login-footer">
          <span>{t("login.privateWorkspace")}</span>
          <img src="/brand/prismic-logo.webp" alt="Prismic Media" />
        </div>
      </section>
    </main>
  );
}
